<template src="./review-toolbar.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import flagReviewService from 'services/api/flag-review'
import voteReviewService from 'services/api/vote-for-review'
import formatName from 'filters/format-name'
import globalStore from 'init/global-store'
import logger from 'util/logger'
import Menu from 'objects/menu/Menu.vue'
import MenuList, { IMenuListItem } from 'objects/menu/MenuList.vue'
import { RestError } from 'services/api-service'
import { getSessionVarInt, setSessionVar, removeSessionVar,
  REVIEW_DOWNVOTED_TYPE, REVIEW_UPVOTED_TYPE } from 'util/session-storage'
import trackingService from 'services/tracking-service'

export default {
  data() {
    return {
      wasDownVoted: false,
      wasFlagged: false,
      wasUpVoted: false,
      globalStoreState: globalStore.state,
      userSessionDownVote: this.downVotes,
      userSessionUpVote: this.upVotes,
      voteBeingProcessed: false
    }
  },
  props: {
    checkoutSiteKey: String,
    downVotes: { type: Number, required: true },
    review: { type: Object, required: true },
    upVotes: { type: Number, required: true },
    isRelatedReview: { type: Boolean, required: false }
  },
  components: {
    'tt-menu': Menu,
    'tt-menu-list': MenuList
  },
  computed: {
    downVoteCount() {
      return this.userSessionDownVote
    },
    downVoteIconName() {
      return this.wasDownVoted ? 'unhelpful-on' : 'unhelpful-off'
    },
    flagAriaLabel() {
      return this.$msg(this.wasFlagged ?
        'review.flagged.aria' : 'review.flag.aria', [this.username])
    },
    flagIconName() {
      return this.wasFlagged ? 'flag-on' : 'flag-off'
    },
    flagText() {
      return this.$msg(this.wasFlagged ? 'review.flagged' : 'review.flag')
    },
    menuItems() {
      const flagText = this.$msg(this.wasFlagged ? 'review.flagged' : 'review.flag')
      return [
        { name: 'flag', iconName: this.flagIconName, title: flagText, disabled: this.wasFlagged }
      ] as Array<IMenuListItem>
    },
    upVoteCount() {
      return this.userSessionUpVote
    },
    upVoteIconName() {
      return this.wasUpVoted ? 'helpful-on' : 'helpful-off'
    },
    username() {
      return formatName(this.review.user)
    },
    wasVotedOn() {
      return this.wasDownVoted || this.wasUpVoted
    }
  },
  methods: {
    downVote(e) {
      const voteBtn = e.currentTarget
      if (this.wasDownVoted) {
        // was down voted, now the user is retracting that vote
        this.sendVote(0, -1, voteBtn)
      } else {
        // actual down vote
        // need to check if up vote already given by this user.  If so, need to retract
        const up = this.wasUpVoted ? -1 : 0
        this.sendVote(up, 1, voteBtn)
      }
    },
    flag() {
      if (this.wasFlagged) {
        return
      }
      this.wasFlagged = true
      flagReviewService.flagReview({
        id: this.review.id,
        checkoutSiteKey: this.checkoutSiteKey || '',
        sku: this.review.catItem.sku
      }).then(() => {
        trackingService.queueEvent(this.isRelatedReview ? 'related-reviews-list' : 'reviews-list',
          'flag', this.review.catItem.sku, { rid: this.review.id })
      }).catch((e: RestError) => {
        this.wasFlagged = false
        let msgKey =  'unexpected.error'
        if (e.errorCode === 'turnto-offline') {
          msgKey = 'offline.error'
        } else {
          logger.warn('Error sending review flag', e)
        }
        this.showErrorPopover(this.$msg(msgKey))
      })
    },
    menuItemSelected(item) {
      if (item.name === 'flag' && !this.wasFlagged) {
        this.flag()
      }
    },
    removeUserDownVote() {
      if (getSessionVarInt(REVIEW_DOWNVOTED_TYPE, this.review.id)) {
        this.userSessionDownVote -= 1
      }
      removeSessionVar(REVIEW_DOWNVOTED_TYPE, this.review.id)
    },
    removeUserUpVote() {
      if (getSessionVarInt(REVIEW_UPVOTED_TYPE, this.review.id)) {
        this.userSessionUpVote -= 1
      }
      removeSessionVar(REVIEW_UPVOTED_TYPE, this.review.id)
    },
    sendVote(up: number, down: number, voteBtn) {
      if (this.voteBeingProcessed) {
        return
      }
      this.voteBeingProcessed = true
      return voteReviewService.voteForReview({
        id: this.review.id,
        checkoutSiteKey: this.checkoutSiteKey || '',
        sku: this.review.catItem.sku
      }, {
        down,
        up
      }).then(() => {
        this.voteBeingProcessed = false
        this.wasUpVoted = up === 1 || (this.wasUpVoted && !(up === -1))
        if (this.wasUpVoted) {
          this.userSessionUpVote += 1
          setSessionVar(REVIEW_UPVOTED_TYPE, this.review.id, this.userSessionUpVote)
          this.removeUserDownVote()
        } else {
          this.removeUserUpVote()
        }
        this.wasDownVoted = down === 1 || (this.wasDownVoted && !(down === -1))
        if (this.wasDownVoted) {
          this.userSessionDownVote += 1
          setSessionVar(REVIEW_DOWNVOTED_TYPE, this.review.id, this.userSessionDownVote)
          this.removeUserUpVote()
        } else {
          this.removeUserDownVote()
        }
        nextTick(() => voteBtn.focus())
        const voteType = this.voteEventType(up, down)
        trackingService.queueEvent(this.isRelatedReview ? 'related-reviews-list' : 'reviews-list',
          'vote', this.review.catItem.sku,
          { rid: this.review.id, vote: voteType })
      }).catch((e: RestError) => {
        this.voteBeingProcessed = false
        let msgKey =  'unexpected.error'
        if (e.errorCode === 'turnto-offline') {
          msgKey = 'offline.error'
        } else {
          logger.warn('Error sending vote', e)
        }
        this.showErrorPopover(this.$msg(msgKey))
      })
    },
    showErrorPopover(message: string) {
      import(/* webpackChunkName: 'standalone-popover' */ 'widgets/standalone-popover/standalone-popover-loader').then((popoverLoader) => {
        popoverLoader.default.load({ message, type: 'error' })
      })
    },
    upVote(e) {
      const voteBtn = e.currentTarget
      if (this.wasUpVoted) {
        // was up voted, now the user is retracting that vote
        this.sendVote(-1, 0, voteBtn)
      } else {
        // actual up vote
        // need to check if down vote already given by this user.  If so, need to retract
        const down = this.wasDownVoted ? -1 : 0
        this.sendVote(1, down, voteBtn)
      }
    },
    voteEventType(up, down) {
      if (up === 1 && down === 0) {
        return 'UP'
      } else if (down === 1 && up === 0) {
        return 'DOWN'
      } else if (up === -1 && down === 0) {
        return 'RM UP'
      } else if (up === 0 && down === -1) {
        return 'RM DOWN'
      } else if (up === 1 && down === -1) {
        return 'SW UP'
      } else if (down === 1 && up === -1) {
        return 'SW DOWN'
      }
    }
  },
  created() {
    const rWasDownVoted = getSessionVarInt(REVIEW_DOWNVOTED_TYPE, this.review.id)
    if (rWasDownVoted !== null) {
      this.wasDownVoted = true
      this.userSessionDownVote = rWasDownVoted
    }
    const rWasUpVoted = getSessionVarInt(REVIEW_UPVOTED_TYPE, this.review.id)
    if (rWasUpVoted !== null) {
      this.wasUpVoted = true
      this.userSessionUpVote = rWasUpVoted
    }
  }
}
</script>

<style lang="scss" src="./review-toolbar.scss"></style>
